import React, { useState, useEffect, useRef } from 'react';
import ColorThief from 'colorthief';
import { useDiskColor } from '../contexts/DiskColorContext';
import { useImageContext } from '../contexts/ImageContext';
import './css/BackgroundColor.css';

const BackgroundColor = () => {
    const { diskColor, setDiskColor } = useDiskColor();
    const { currentImage } = useImageContext();
    const [color, setColor] = useState(diskColor); // For color picker and hex input
    const [palette, setPalette] = useState([]);
    // Show palette by default on desktop (>768px), hidden on mobile
    const [showPalette, setShowPalette] = useState(typeof window !== 'undefined' && window.innerWidth > 768);
    const imgRef = useRef(null);

    // Convert RGB array from Color Thief to HEX
    const rgbToHex = (rgb) => {
      if (!rgb || !Array.isArray(rgb) || rgb.length < 3) return '#000000';
      return `#${rgb.map((x) => {
        const hex = Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0');
        return hex;
      }).join('')}`;
    };

    useEffect(() => {
        const imgElement = imgRef.current;
        const updatePalette = () => {
            if (imgElement && imgElement.complete) {
                const colorThief = new ColorThief();
                const extractedPalette = colorThief.getPalette(imgElement, 5); // Gets an array of colors
                setPalette(extractedPalette.map(rgbToHex));
            }
        };

        if (imgElement) {
            imgElement.addEventListener('load', updatePalette);
            if (imgElement.complete) updatePalette();
        }

        return () => imgElement?.removeEventListener('load', updatePalette);
    }, [currentImage]);

    // Limit palette to 4 colors on mobile
    const displayPalette = typeof window !== 'undefined' && window.innerWidth <= 768 
        ? palette.slice(0, 4) 
        : palette;

    const handleColorChange = (event) => {
        setColor(event.target.value);
        setDiskColor(event.target.value);
    };

    const handlePaletteColorSelect = (hexColor) => {
        setColor(hexColor);
        setDiskColor(hexColor);
    };

    return (
        <div className='background-color-container'>
            <div className="color-picker-container">
                <label className="color-picker-wrapper">
                    <input
                        type="color"
                        className="background-color-selector"
                        onChange={handleColorChange}
                        value={color}
                        title="Click to choose a color"
                    />
                    <span className="color-picker-label">Pick a colour</span>
                </label>
                {palette.length > 0 && (
                    <button
                        className="palette-toggle"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowPalette(!showPalette);
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowPalette(!showPalette);
                        }}
                        type="button"
                    >
                        <span className='background-color-text'>View colour suggestions</span>
                        <span className="palette-toggle-icon">{showPalette ? '▼' : '▶'}</span>
                    </button>
                )}
            </div>
            {palette.length > 0 && showPalette && (
                <div className="color-palette">
                    {displayPalette.map((hexColor, index) => (
                        <button
                            key={index}
                            className="color-swatch"
                            style={{ backgroundColor: hexColor }}
                            onClick={() => handlePaletteColorSelect(hexColor)}
                            title={`Select color ${hexColor}`}
                        />
                    ))}
                </div>
            )}
            <img ref={imgRef} src={currentImage} crossOrigin="anonymous" alt="Color source" style={{ display: 'none' }} />
        </div>
    );
};

export default BackgroundColor;
